// Cash-ownership screening only. USD nominal cash flows, year-end convention.
export const defaults = Object.freeze({ monthlySpend:10000,buildingSqFt:30000,utility:'FPL',roofType:'TPO',usableRoofFraction:.55,wattsPerUsableSqFt:12,annualKWh:null,energyBillFraction:.65,importRate:.15,exportRate:0,exportTermsConfirmed:false,selfUseFraction:.8,annualYield:1450,dollarsPerWatt:2.5,annualOMFraction:.015,annualDegradation:.005,energyEscalation:.02,discountRate:.08,taxRate:.21,taxScheduleConfirmed:false,creditRate:0,creditUseYear:1,depreciationFractions:Array(26).fill(0),intervalStudyConfirmed:false,annualDemandSavings:0,replacementCost:0,replacementYear:13,financeMode:'cash' });
export function estimate(input={}) {
 const x={...defaults,...input};
 for(const k of ['monthlySpend','buildingSqFt','annualYield','dollarsPerWatt','importRate']) if(!Number.isFinite(x[k])||x[k]<=0)throw Error(k+' must be positive');
 for(const k of ['usableRoofFraction','selfUseFraction','energyBillFraction','annualOMFraction','annualDegradation','taxRate','creditRate'])if(!Number.isFinite(x[k])||x[k]<0||x[k]>1)throw Error('Invalid '+k);
 for(const k of ['exportRate','annualDemandSavings','replacementCost'])if(!Number.isFinite(x[k])||x[k]<0)throw Error('Invalid '+k);
 if(!Number.isFinite(x.wattsPerUsableSqFt)||x.wattsPerUsableSqFt<=0)throw Error('Invalid roof density');
 if(!Number.isFinite(x.discountRate)||x.discountRate<=-1||x.discountRate>1||!Number.isFinite(x.energyEscalation)||x.energyEscalation<=-1||x.energyEscalation>1)throw Error('Invalid discount/escalation rate');
 for(const k of ['creditUseYear','replacementYear'])if(!Number.isInteger(x[k])||x[k]<1||x[k]>25)throw Error('Invalid '+k);
 if(!['cash','cpace','ppa'].includes(x.financeMode))throw Error('Invalid ownership mode');
 if(x.annualKWh!==null&&(!Number.isFinite(x.annualKWh)||x.annualKWh<=0))throw Error('Invalid annual kWh');
 const dep=x.depreciationFractions;
 if(!Array.isArray(dep)||dep.length!==26||dep[0]!==0||dep.some(v=>!Number.isFinite(v)||v<0)||dep.reduce((a,b)=>a+b,0)>1.000000001)throw Error('Use a 26-entry deduction schedule, index 0 = 0; fractions total <= 1');
 const warnings=['Illustrative cash-ownership model; not a tariff simulation or engineering proposal.','All cost, yield and escalation defaults are demonstration assumptions, not sourced market quotes.','Fixed charges, financing, property-tax savings and storage are excluded.','Net operating savings are reduced by the entered effective tax rate; tax capacity and deductibility require review.'];
 const load=x.annualKWh??x.monthlySpend*12*x.energyBillFraction/x.importRate;
 if(x.annualKWh===null)warnings.push('Annual usage inferred from assumed energy-only share of spend; replace with bills.');
 const systemKW=Math.min(x.buildingSqFt*x.usableRoofFraction*x.wattsPerUsableSqFt/1000,load/x.annualYield);
 const grossCost=systemKW*1000*x.dollarsPerWatt;
 const owned=x.financeMode!=='ppa';
 const credit=owned&&x.taxScheduleConfirmed?grossCost*x.creditRate:0;
 const adjustedBasis=grossCost-credit*.5;
 const demand=x.intervalStudyConfirmed?x.annualDemandSavings:0;
 if(demand>x.monthlySpend*12*(1-x.energyBillFraction))throw Error('Demand savings exceed remaining assumed demand/fixed budget');
 if(!x.exportTermsConfirmed)warnings.push('Export value is zero until applicable utility terms are confirmed.');
 if(!x.taxScheduleConfirmed)warnings.push('Tax benefits are zero until a project-specific schedule is confirmed.');
 if(x.financeMode!=='cash')warnings.push('Financed/PPA NPV and payback suppressed: use a separate financing schedule.');
 const cashflows=[{year:0,cashFlow:-grossCost,cumulative:-grossCost,presentValue:-grossCost}];
 let cumulative=-grossCost,npv=-grossCost,payback=null;
 for(let year=1;year<=25;year++){
  const generation=systemKW*x.annualYield*(1-x.annualDegradation)**(year-1);
  const onsite=Math.min(load,generation*x.selfUseFraction),exports=Math.max(0,generation-onsite);
  const energySavings=Math.min(load*x.importRate,onsite*x.importRate)*(1+x.energyEscalation)**(year-1);
  const exportRevenue=exports*(x.exportTermsConfirmed?x.exportRate:0)*(1+x.energyEscalation)**(year-1);
  const om=grossCost*x.annualOMFraction*(1+x.energyEscalation)**(year-1);
  const replacement=year===x.replacementYear?x.replacementCost:0;
  const creditUsed=year===x.creditUseYear?credit:0;
  const deductionTaxValue=owned&&x.taxScheduleConfirmed?adjustedBasis*dep[year]*x.taxRate:0;
  const operating=energySavings+exportRevenue+demand-om;
  const taxShield=creditUsed+deductionTaxValue;
  const cashFlow=operating*(1-x.taxRate)-replacement+taxShield;
  const before=cumulative;cumulative+=cashFlow;
  if(payback===null&&before<0&&cumulative>=0&&cashFlow>0)payback=year-1+(-before/cashFlow);
  const presentValue=cashFlow/(1+x.discountRate)**year;npv+=presentValue;
  cashflows.push({year,generation,onsite,exports,energySavings,exportRevenue,demandSavings:demand,om,replacement,creditUsed,deductionTaxValue,taxShield,cashFlow,cumulative,presentValue});
 }
 return {inputs:x,systemKW,grossCost,annualLoadKWh:load,adjustedBasis,credit,yearOneTaxShield:cashflows[1].taxShield,yearOneOperatingSavings:cashflows[1].energySavings+cashflows[1].exportRevenue+demand-cashflows[1].om,npv25:x.financeMode==='cash'?npv:null,paybackYears:x.financeMode==='cash'?payback:null,cashflows:x.financeMode==='cash'?cashflows:[],warnings};
}

/** Adviser-confirmed 5-year MACRS-style schedule (half-year convention), illustration only. */
export const MACRS_5Y = [0, .2, .32, .192, .1152, .1152, .0576, ...Array(19).fill(0)];

/**
 * Bridge: engine input shape (DEFAULT_INPUTS from finance.js) -> canonical estimate().
 * Keeps the dashboard's result shape so the UI is unchanged, but every number now
 * comes from the one shared 25-year cash-ownership model.
 */
export function calculateFinancialsUnified(x) {
  const dep = Array(26).fill(0);
  dep[1] = Math.min(Math.max(Number(x.firstYearDeductionFraction) || 0, 0), 1);
  const input = {
    monthlySpend: x.monthlySpend,
    buildingSqFt: x.roofSqFt,
    usableRoofFraction: 1, // engine roofSqFt is already usable area
    wattsPerUsableSqFt: x.roofWattsPerSqFt,
    annualYield: x.annualKWhPerKW,
    importRate: x.energyRate,
    energyBillFraction: 1 - (x.demandShare ?? 0),
    selfUseFraction: x.selfConsumption,
    dollarsPerWatt: x.dollarsPerWatt,
    annualOMFraction: x.annualOperatingCostFraction,
    taxRate: x.taxRate,
    taxScheduleConfirmed: Boolean(x.eligibilityConfirmed && x.taxBenefitsUsable),
    creditRate: x.creditRate,
    creditUseYear: 1,
    depreciationFractions: dep,
    financeMode: x.financing === "ppa" ? "ppa" : x.financing === "cpace" ? "cpace" : "cash",
    intervalStudyConfirmed: Boolean(x.intervalStudyConfirmed),
    annualDemandSavings: Number(x.annualDemandSavings) || 0,
    exportRate: 0,
    exportTermsConfirmed: false,
    replacementCost: 0,
    replacementYear: 13,
    discountRate: 0.08,
  };
  const r = estimate(input);
  const ownership = input.financeMode === "cash";
  return {
    ...r,
    systemKW: r.systemKW,
    grossCost: r.grossCost,
    yearOneOperatingSavings: r.yearOneOperatingSavings,
    taxShield: ownership ? r.cashflows.reduce((s, y) => s + (y.taxShield || 0), 0) : 0,
    demandSavings: r.cashflows.length ? r.cashflows[1].demandSavings : 0,
    taxBenefitsApplied: ownership && input.taxScheduleConfirmed,
    annualNetOperatingSavings: r.yearOneOperatingSavings,
    simplePayback: input.financeMode === "cash" ? r.paybackYears : null,
    paybackSuppressed: input.financeMode !== "cash",
    npv25: r.npv25,
    valuationSensitivity: x.ownerPaysPower && x.capRate > 0
      ? r.yearOneOperatingSavings * (1 - x.taxRate) / x.capRate
      : null,
    ownership: ownership,
    modelVersion: "florida-cash-v1",
    warnings: r.warnings,
  };
}
